import React, { useMemo, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useLanguage } from '../../../../contexts/LanguageContext';

const CurrentAccountReportTable = ({
  rows = [],
  loading = false,
  totalCount = 0,
  pagination = { PageNumber: 1, PageSize: 10 },
  onPageChange = () => {}
}) => {
  const { th } = useLanguage();
  const dataTableRef = useRef(null);

  const [sortField, setSortField] = useState('musteriAdSoyad');
  const [sortOrder, setSortOrder] = useState(1);

  const normalized = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const sorted = useMemo(() => {
    if (!sortField || sortOrder === 0) return normalized;
    const copy = [...normalized];
    return copy.sort((a, b) => {
      let av = a?.[sortField],
        bv = b?.[sortField];
      if (av == null && bv == null) return 0;
      if (av == null) return sortOrder;
      if (bv == null) return -sortOrder;
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return -sortOrder;
      if (av > bv) return sortOrder;
      return 0;
    });
  }, [normalized, sortField, sortOrder]);

  const handleSort = (e) => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder);
  };

  const formatCurrency = (value) => {
    if (value == null) return '';
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const borcTemplate = (rowData) => formatCurrency(rowData.borc);
  const alacakTemplate = (rowData) => formatCurrency(rowData.alacak);
  const bakiyeTemplate = (rowData) => formatCurrency(rowData.bakiye);

  const borcAlacakTemplate = (rowData) => {
    if (rowData.borcAlacak === 'B') return <span className="text-red-500 font-semibold">{th('currentAccountReport.debit') || 'Borç'}</span>;
    if (rowData.borcAlacak === 'A') return <span className="text-green-500 font-semibold">{th('currentAccountReport.credit') || 'Alacak'}</span>;
    return rowData.borcAlacak;
  };

  return (
    <DataTable
      ref={dataTableRef}
      value={sorted}
      dataKey="customerId"
      lazy
      paginator
      first={(pagination?.PageNumber - 1) * (pagination?.PageSize || 10)}
      rows={pagination?.PageSize || 10}
      totalRecords={totalCount}
      onPage={onPageChange}
      onSort={handleSort}
      sortField={sortField}
      sortOrder={sortOrder}
      loading={loading}
      rowsPerPageOptions={[5, 10, 25, 50, 100]}
      tableStyle={{ minWidth: '60rem' }}
      showGridlines
      stripedRows
      sortMode="single"
      emptyMessage={th('currentAccountReport.empty') || 'Kayıt bulunamadı'}
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      currentPageReportTemplate={th('table.paginatorTemplate')}
    >
      <Column field="musteriAdSoyad" header={th('currentAccountReport.customerName') || 'Müşteri Adı Soyadı'} sortable />
      <Column field="phoneNumber" header={th('currentAccountReport.phoneNumber') || 'Telefon Numarası'} sortable style={{ width: 160 }} />
      <Column field="borc" header={th('currentAccountReport.debitAmount') || 'Borç'} body={borcTemplate} sortable style={{ width: 140, textAlign: 'right' }} />
      <Column field="alacak" header={th('currentAccountReport.creditAmount') || 'Alacak'} body={alacakTemplate} sortable style={{ width: 140, textAlign: 'right' }} />
      <Column field="borcAlacak" header={th('currentAccountReport.debitCredit') || 'B/A'} body={borcAlacakTemplate} sortable style={{ width: 100, textAlign: 'center' }} />
      <Column field="bakiye" header={th('currentAccountReport.balance') || 'Bakiye'} body={bakiyeTemplate} sortable style={{ width: 150, textAlign: 'right' }} />
      <Column field="currency" header={th('currentAccountReport.currency') || 'Döviz'} sortable style={{ width: 100, textAlign: 'center' }} />
      <Column field="plaka" header={th('currentAccountReport.licensePlate') || 'Plaka'} sortable style={{ width: 140 }} />
    </DataTable>
  );
};

export default CurrentAccountReportTable;
